import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { AccessKey } from 'src/features/accessKey/utils/types';
import { accessKeyFormSchema, AccessKeyFormSchemaValues } from 'src/features/accessKey/utils/formSchema';
import { useGetAccessKey } from 'src/features/accessKey/api/useGetAccessKey';
import AccessKeyForm from 'src/features/accessKey/components/form/AccessKeyForm';
import { useParking } from 'src/providers/ParkingProvider';

interface AccessKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccessKey?: AccessKey;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: AccessKeyFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function AccessKeyFormModal<TData>({
    selectedAccessKey,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: AccessKeyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { data: accessKeyData } = useGetAccessKey({
        id: selectedAccessKey?.id ?? '',
        parkingId,
        config: { enabled: !!selectedAccessKey?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            type: accessKeyData?.type ?? undefined,
            value: accessKeyData?.value ?? undefined,
            valid_from: accessKeyData?.valid_from ?? undefined,
            valid_to: accessKeyData?.valid_to ?? undefined,
            pass_back_allowed: accessKeyData?.pass_back_allowed ?? undefined,
        };
    }, [accessKeyData]);

    const form = useForm<AccessKeyFormSchemaValues>({
        resolver: zodResolver(accessKeyFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [accessKeyData, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: AccessKeyFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, formatMessage, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AccessKeyForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedAccessKey && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AccessKeyFormModal;
